import {
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  FormLabel,
  TextField,
  MenuItem,
  Autocomplete,
} from "@mui/material";
import LoadingSpinner from "../../../App/Components/LoadingSpinner/LoadingSpinner";
import { useEffect, useState } from "react";
import { ICandidate } from "../../../App/Typings/Candidate";
import { useCandidatesQuery } from "../../../App/Queries/CandidateQueries";
import { useGroupMembersQuery, useGroupsQuery } from "../../../App/Queries/GroupQueries";

export interface CandidatesStepProps {
  onChange: (candidates: ICandidate[]) => void;
  selectedCandidates: ICandidate[];
  isLoading: (isLoading: boolean) => void;
}

export default function CandidatesStep({ onChange, selectedCandidates, isLoading }: CandidatesStepProps) {
  const [isAllCandidatesChecked, setIsAllCandidatesChecked] = useState<boolean>(false);
  const [selectedGroup, setSelectedGroup] = useState<string>("");

  const { data: candidateList, isFetched: candidatesFetched } = useCandidatesQuery(true);
  const { data: groups, isFetched: isGroupsFetched } = useGroupsQuery();
  const { data: groupMembers, isFetched } = useGroupMembersQuery(selectedGroup);

  useEffect(() => {
    if (
      candidatesFetched &&
      selectedCandidates.length === candidateList?.items.length &&
      selectedCandidates.length > 0
    ) {
      setIsAllCandidatesChecked(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidatesFetched]);

  useEffect(() => {
    if (isFetched && groupMembers) {
      const groupCandidates =
        candidateList?.items.filter((c) => groupMembers.items.some((gm) => gm.candidateId === c.id)) ?? [];
      onChange(groupCandidates);
      isLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetched, groupMembers]);

  const handleAllCandidatesChecked = (_event: React.ChangeEvent<HTMLInputElement>, isChecked: boolean) => {
    setIsAllCandidatesChecked(isChecked);
    if (isChecked) {
      onChange(candidateList?.items ?? []);
    } else {
      onChange([]);
    }
  };

  const handleSelectedGroupChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.target.value === "") {
      onChange([]);
    } else {
      isLoading(true);
    }
    setSelectedGroup(event.target.value);
  };

  const getSortedCandidates = () => {
    const sortedCandidates = candidateList?.items ?? [];

    sortedCandidates.sort((a, b) => {
      if (a.firstName.toLowerCase() < b.firstName.toLowerCase()) {
        return -1;
      }

      return a.firstName.toLowerCase() > b.firstName.toLowerCase() ? 1 : 0;
    });

    return sortedCandidates;
  };

  const handleCandidateAutocompleteChange = (
    _ev: React.SyntheticEvent<Element, Event>,
    value: ICandidate[],
  ) => {
    onChange(value);
  };

  return (
    <Box sx={{ display: "flex", mb: 1, flexDirection: "column" }}>
      <Typography variant="body1" sx={{ mb: 2 }}>
        Select candidates to send campaign emails to by selecting all candidates, a group, or specific
        candidates.
      </Typography>
      {!candidatesFetched || !isGroupsFetched ? (
        <LoadingSpinner />
      ) : (
        <>
          <FormControlLabel
            control={
              <Checkbox
                onChange={handleAllCandidatesChecked}
                checked={isAllCandidatesChecked}
                disabled={selectedGroup !== "" || (!isAllCandidatesChecked && selectedCandidates.length > 0)}
              />
            }
            label="All Candidates"
          />
          <>
            <FormLabel sx={{ mb: 2, mt: 2, fontWeight: 600 }} id="group-select-label" htmlFor="group-select">
              Groups
            </FormLabel>
            <TextField
              id="group-select"
              select
              disabled={isAllCandidatesChecked || (selectedGroup === "" && selectedCandidates.length > 0)}
              onChange={handleSelectedGroupChange}
              value={selectedGroup ?? ""}
            >
              <MenuItem key={"none"} value={""} data-test={"campaign-group-select-none"}>
                None
              </MenuItem>
              {groups
                ? groups.items.map((c) => (
                    <MenuItem key={c.id} value={c.id} data-test={`campaign-group-select-${c.name}`}>
                      {c.name}
                    </MenuItem>
                  ))
                : []}
            </TextField>
          </>
          <>
            <FormLabel
              sx={{ mb: 2, mt: 2, fontWeight: 600 }}
              id="candidate-multi-select-label"
              htmlFor="candidate-multi-select"
            >
              Candidates
            </FormLabel>
            <Autocomplete
              disabled={isAllCandidatesChecked || selectedGroup !== ""}
              multiple
              id="candidate-multi-select"
              options={getSortedCandidates()}
              getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
              onChange={handleCandidateAutocompleteChange}
              value={isAllCandidatesChecked || selectedGroup ? [] : selectedCandidates}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder={
                    selectedCandidates.length > 0 && !isAllCandidatesChecked ? undefined : "...John Doe"
                  }
                />
              )}
              renderOption={(props, option) => {
                return (
                  <li {...props} key={option.id}>
                    {option.firstName} {option.lastName}
                  </li>
                );
              }}
            />
          </>
        </>
      )}
    </Box>
  );
}
